import { Box, Button, Stack, styled, Tab, Tabs } from "@mui/material";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useRegions } from "hooks/useRegions";
import { ReactComponent as EditIcon } from "images/edit-big-icon.svg";
import { ReactComponent as UpdatedIcon } from "images/updated-status-icon.svg";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GlobalContext } from "store/globalContext";
import { EditPracticeDialog } from "./EditPracticeDialog";
import { ModalSpinner } from "./ModalSpinner";
import * as SC from "./RecipientDetails.style";
import * as CSC from "SC";
import { useTabs } from "hooks/useTabs";
import { TabPanel } from "./TabPanel";
import { DataGrid } from "UI/DataGrid";
import { useDataGrid } from "hooks/useDataGrid";
import {
  buttonColumns,
  mobileButtonColumns,
} from "./RecipientDetailsButtons.config";
import { EditButtonLocationSuperAdminDialog } from "./EditButtonLocationSuperAdminDialog";
import { EditUserDialog } from "./EditUserDialog";
import {
  usersColumns,
  usersMobileColumns,
} from "./RecipientDetailsUsers.config";
import { AddNewRecipientUserDialog } from "./AddNewRecipientUserDialog";
import {
  recipientNotificationsColumns,
  recipientNotificationsMobileColumns,
} from "./RecipientDetailsNotifications.config";
import {
  recipientQuestionsColumns,
  recipientQuestionsMobileColumns,
} from "./RecipientDetailsQuestions.config";
import {
  mobileReferralsColumns,
  referralsColumns,
} from "./RecipientDetailsReferrals.config";
import {
  archiveColumns,
  archiveMobileColumns,
} from "./RecipientDetailsArchive.config";
import { billingColumns, mobileBillingColumns } from "./RecipientDetailsBilling.config";
import { mobilePaymentColumns, paymentColumns } from "./RecipientDetailsPayments.config";
import { locationsColumns, locationsMobileColumns } from "./RecipientDetailsLocations.config";

const EditBigIcon = styled(EditIcon)`
  cursor: pointer;
`;

export const RecipientDetails = () => {
  const { createApiCall } = useCreateApiCall();
  const navigate = useNavigate();
  const { showWarning, userData } = useContext(GlobalContext);
  const { regionsOptions, refreshRegionsOptions } = useRegions();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isEditPracticeOpen, setIsEditPracticeOpen] = useState(false);
  const { currentTab, handleChangeTab } = useTabs({
    key: "RecipientDetails",
  });
  const [currentButtonId, setCurrentButtonId] = useState("");
  const [selectedUser, setSelectedUser] = useState({});
  const [isAddNewDialogOpen, setIsAddNewDialogOpen] = useState(false);

  const {
    name,
    speciality,
    phone,
    email,
    street,
    street2,
    city,
    state,
    zipCode,
    website,
    notification_price,
    question_price,
    referral_price,
    button_device_price,
  } =
    // @ts-ignore
    data?.Item ?? {};

  const getData = createApiCall(async () => {
    // load recipient details by recipient id
    const result = await API.get("restApi", `/recipients/${id}`, {});
    setData(result);
  });

  const allowRecipientAccess = createApiCall(async () => {
    const result = await API.post(
      "restApi",
      `/allow_recipient_access/${id}`,
      {}
    );
    if (result?.error) {
      return;
    }
    showWarning({
      title: "Recipient access allowed successfully",
      asNotification: true,
      icon: <UpdatedIcon />,
    });
    setTimeout(() => getData(), 0);
  });

  const blockRecipientAccess = createApiCall(async () => {
    const result = await API.post(
      "restApi",
      `/block_recipient_access/${id}`,
      {}
    );
    if (result?.error) {
      return;
    }
    showWarning({
      title: "Recipient access blocked successfully",
      asNotification: true,
      icon: <UpdatedIcon />,
    });
    setTimeout(() => getData(), 0);
  });

  const usersDataGrid = useDataGrid({
    url: "/users/list",
    defaultSorter: {
      name: "name",
      order: "asc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-Users`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
      }),
      [id]
    ),
  });

  const handleRowClick = (row) => {
    setSelectedUser(row);
  };

  const buttonsDataGrid = useDataGrid({
    url: "/all_button_locations",
    defaultSorter: {
      name: "practiceName",
      order: "asc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-Buttons`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
      }),
      [id]
    ),
  });

  const handleButtonRowClick = ({ id }) => {
    setCurrentButtonId(id);
  };

  const notificationsDataGrid = useDataGrid({
    url: "/referrals",
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-Notifications`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
        type: "Notification",
      }),
      [id]
    ),
  });

  const questionsDataGrid = useDataGrid({
    url: "/referrals",
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-Questions`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
        type: "Question",
      }),
      [id]
    ),
  });

  const referralsDataGrid = useDataGrid({
    url: "/referrals",
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-Referrals`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
        type: "Referral",
      }),
      [id]
    ),
  });

  const archiveDataGrid = useDataGrid({
    url: "/referrals",
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-Archive`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
        type: "Archive",
      }),
      [id]
    ),
  });

  const handleReferralRowClick = ({ id }) => {
    navigate(`/referral/${id}`);
  };

  const billingDataGrid = useDataGrid({
    url: "/invoices",
    defaultSorter: {
      name: "invoice_date",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-billing`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
      }),
      [id]
    ),
  });

  const paymentDataGrid = useDataGrid({
    url: "/payment/find",
    defaultSorter: {
      name: "created",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-payments`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
      }),
      [id]
    ),
    restApi: "restApiV2",
  });

  const locationDataGrid = useDataGrid({
    url: "/locations",
    defaultSorter: {
      name: "name",
      order: "asc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-Recipient-location`,
    additionalParams: useMemo(
      () => ({
        recipientId: id,
      }),
      [id]
    ),
  });

  const handleInvoiceRowClick = ({ id }) => {
    navigate(`/invoice/${id}`);
  };

  const handlePaymentRowClick = ({ invoiceId }) => {
    navigate(`/invoice/${invoiceId}`);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    switch (currentTab) {
      case 0:
        usersDataGrid.loadData();
        break;
      case 1:
        buttonsDataGrid.loadData();
        break;
      case 2:
        notificationsDataGrid.loadData();
        break;
      case 3:
        questionsDataGrid.loadData();
        break;
      case 4:
        referralsDataGrid.loadData();
        break;
      case 5:
        archiveDataGrid.loadData();
        break;
      case 6:
        billingDataGrid.loadData();
        break;
      case 7:
        paymentDataGrid.loadData();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  return (
    <CommonOutletTemplate title={"Recipient Details"} backIcon>
      {data ? (
        <div className="flex-grow flex-shrink-0 sm:flex-shrink flex flex-col gap-4 p-0 overflow-hidden">
          <div className="flex flex-row justify-between flex-wrap items-center gap-4">
            <div className="flex flex-row items-center gap-4">
              {/* <div className="block lg:hidden">
                <BackToPrevPage isMobile backLink={"/"} />
              </div> */}
              <Stack
                mt={2}
                flex={1}
                direction={"row"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                gap={2}
              >
                <SC.Title>{name}</SC.Title>
                <EditBigIcon onClick={() => setIsEditPracticeOpen(true)} />
              </Stack>
            </div>
            {data?.Item?.isBlocked ? (
              <>
                <Box color="red">Suspended</Box>
                <Button
                  variant="outlined"
                  color={"success"}
                  onClick={allowRecipientAccess}
                >
                  Allow Recipient Access
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                color={"warning"}
                onClick={blockRecipientAccess}
              >
                Suspend Recipient Access
              </Button>
            )}
          </div>
          <div
            className={`
              flex flex-col sm:flex-row gap-4 justify-between
              bg-white border border-solid border-gray-300 rounded-2xl p-4 md:p-6`}
          >
            <SC.InfoSection>
              {speciality ? (
                <SC.Info>
                  <SC.Label>Speciality:</SC.Label> {speciality}
                </SC.Info>
              ) : (
                ""
              )}
              <SC.Info>
                <SC.Label>Address:</SC.Label>{" "}
                {street2 ? `${street}, ${street2}` : street}, {city}, {state},{" "}
                {zipCode}
              </SC.Info>
              {!!phone && (
                <SC.Info>
                  <SC.Label>Phone:</SC.Label>{" "}
                  <a href={`tel:${phone}`}>{phone}</a>
                </SC.Info>
              )}
              {!!email && (
                <SC.Info>
                  <SC.Label>Email:</SC.Label>{" "}
                  <a href={`mailto:${email}`}>{email}</a>
                </SC.Info>
              )}
              {!!website && (
                <SC.Info>
                  <SC.Label>Website:</SC.Label>{" "}
                  <a href={website} className="break-all">
                    {website}
                  </a>
                </SC.Info>
              )}
            </SC.InfoSection>
            <SC.InfoSection>
              {/* <SC.Info>
                  <SC.Label>Email:</SC.Label>{" "}
                  <a href={`mailto:${email}`}>{email}</a>
                </SC.Info> */}
              <SC.Info>
                <SC.Label>Notification Price: </SC.Label>${notification_price}
              </SC.Info>
              <SC.Info>
                <SC.Label>Question Price: </SC.Label>${question_price}
              </SC.Info>
              <SC.Info>
                <SC.Label>Referral Price: </SC.Label>${referral_price}
              </SC.Info>
              <SC.Info>
                <SC.Label>Button Device Price: </SC.Label>${button_device_price}
              </SC.Info>
            </SC.InfoSection>
          </div>
          <div
            className={`
              flex-1 bg-white border border-solid border-gray-300 rounded-2xl p-4 md:p-6 pt-2 md:pt-2 overflow-hidden
              flex flex-col justify-between items-stretch
            `}
          >
            <Box sx={{ flex: 0, borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={currentTab}
                onChange={handleChangeTab}
                aria-label="basic tabs example"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="Users" />
                <Tab label="Buttons" />
                <Tab label="Notifications" />
                <Tab label="Questions" />
                <Tab label="Referrals" />
                <Tab label="Archive" />
                <Tab label="Billing" />
                <Tab label="Payments" />
                <Tab label="Locations" />
              </Tabs>
            </Box>
            <div className="flex-1 flex flex-col justify-between items-stretch mt-4 overflow-hidden">
              <TabPanel value={currentTab} index={0}>
                <DataGrid
                  columns={usersColumns}
                  mobileColumns={usersMobileColumns}
                  handleRowClick={handleRowClick}
                  {...usersDataGrid}
                  afterSearchContent={
                    <CSC.BlueButton
                      variant="outlined"
                      onClick={() => setIsAddNewDialogOpen(true)}
                    >
                      Add New User
                    </CSC.BlueButton>
                  }
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={1}>
                <DataGrid
                  columns={buttonColumns}
                  mobileColumns={mobileButtonColumns}
                  handleRowClick={handleButtonRowClick}
                  {...buttonsDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={2}>
                <DataGrid
                  columns={recipientNotificationsColumns}
                  mobileColumns={recipientNotificationsMobileColumns}
                  handleRowClick={handleReferralRowClick}
                  {...notificationsDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={3}>
                <DataGrid
                  columns={recipientQuestionsColumns}
                  mobileColumns={recipientQuestionsMobileColumns}
                  handleRowClick={handleReferralRowClick}
                  {...questionsDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={4}>
                <DataGrid
                  columns={referralsColumns}
                  mobileColumns={mobileReferralsColumns}
                  handleRowClick={handleReferralRowClick}
                  {...referralsDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={5}>
                <DataGrid
                  columns={archiveColumns}
                  mobileColumns={archiveMobileColumns}
                  handleRowClick={handleReferralRowClick}
                  {...archiveDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={6}>
                <DataGrid
                  columns={billingColumns}
                  mobileColumns={mobileBillingColumns}
                  handleRowClick={handleInvoiceRowClick}
                  {...billingDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={7}>
                <DataGrid
                  columns={paymentColumns}
                  mobileColumns={mobilePaymentColumns}
                  handleRowClick={handlePaymentRowClick}
                  {...paymentDataGrid}
                  withRefreshButton
                />
              </TabPanel>
              <TabPanel value={currentTab} index={8}>
                <DataGrid
                  columns={locationsColumns}
                  mobileColumns={locationsMobileColumns}
                  handleRowClick={() => null}
                  {...locationDataGrid}
                  withRefreshButton
                />
              </TabPanel>
            </div>
          </div>
        </div>
      ) : (
        <ModalSpinner open />
      )}

      <EditPracticeDialog
        open={isEditPracticeOpen}
        practiceId={id}
        handleClose={() => setIsEditPracticeOpen(false)}
        refreshList={() => getData()}
        regionsOptions={regionsOptions}
        refreshRegionsOptions={refreshRegionsOptions}
        isRecipient
      />
      {currentButtonId && (
        <EditButtonLocationSuperAdminDialog
          buttonId={currentButtonId}
          handleClose={() => setCurrentButtonId("")}
          refreshList={() => buttonsDataGrid.loadData()}
        />
      )}
      {isAddNewDialogOpen && (
        <AddNewRecipientUserDialog
          open={isAddNewDialogOpen}
          handleClose={() => setIsAddNewDialogOpen(false)}
          refreshList={() => usersDataGrid.loadData()}
          regionsOptions={regionsOptions}
          refreshRegionsOptions={refreshRegionsOptions}
          recipientName={userData?.organization}
          recipientId={id}
        />
      )}
      {
        // @ts-ignore
        selectedUser?.id && (
          <EditUserDialog
            selectedUser={selectedUser}
            handleClose={() => setSelectedUser({})}
            refreshList={usersDataGrid.loadData}
            regionsOptions={regionsOptions}
            refreshRegionsOptions={refreshRegionsOptions}
          />
        )
      }
    </CommonOutletTemplate>
  );
};
